import {createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {getThreadBusinessLinks} from '../../service/ChatBusinessService';

const Context = createContext(null);
const BusinessDialog = lazy(() => import('./ThreadBusinessDialog'));
export const useChatBusiness = () => useContext(Context);

export function ChatBusinessProvider({children, currentUser, conversationId, rootIds}) {
    const enabled = currentUser?.role === 'ADMIN';
    const [draft, setDraft] = useState(null);
    const [links, setLinks] = useState([]);
    const [error, setError] = useState('');
    const [revision, setRevision] = useState(0);
    const refresh = useCallback(() => setRevision(value => value + 1), []);
    const latestScope = useRef('');
    const scope = `${currentUser?.id || 0}:${conversationId || 0}`;
    latestScope.current = scope;
    const idsKey = [...new Set(rootIds.filter(id => Number.isSafeInteger(id) && id > 0))].sort((a, b) => a - b).join(',');
    useEffect(() => { setDraft(null); setLinks([]); setError(''); }, [scope]);
    useEffect(() => {
        if (!enabled || !idsKey) { setLinks([]); return; }
        const controller = new AbortController();
        const ids = idsKey.split(',').map(Number);
        async function load() {
            try {
                const chunks = [];
                for (let offset = 0; offset < ids.length; offset += 100) chunks.push(ids.slice(offset, offset + 100));
                const results = await Promise.all(chunks.map(chunk => getThreadBusinessLinks(chunk, controller.signal)));
                if (!controller.signal.aborted) { setLinks(results.flatMap(result => result.links)); setError(''); }
            } catch (failure) { if (!controller.signal.aborted) setError(failure.message); }
        }
        void load();
        return () => controller.abort();
    }, [enabled, idsKey, revision, scope]);
    useEffect(() => {
        if (!enabled) return;
        const visibleRefresh = () => { if (document.visibilityState === 'visible') refresh(); };
        window.addEventListener('focus', visibleRefresh);
        window.addEventListener('chat-business-saved', visibleRefresh);
        const timer = window.setInterval(visibleRefresh, 30000);
        return () => { window.removeEventListener('focus', visibleRefresh); window.removeEventListener('chat-business-saved', visibleRefresh); window.clearInterval(timer); };
    }, [enabled, refresh]);
    const open = useCallback((kind, message, link = null) => setDraft({kind, rootId: message.thread_root_message_id || message.id, link, requestId: crypto.randomUUID(), scope: latestScope.current}), []);
    const context = useMemo(() => enabled ? {open, links} : null, [enabled, open, links]);
    return <Context.Provider value={context}>
        {children}
        {enabled && error ? <div className="chat-business-error" role="alert">Unable to load Task / Complaint links. <button type="button" onClick={refresh}>Retry</button></div> : null}
        {enabled && draft && draft.scope === scope ? <Suspense fallback={<div role="status">Loading form…</div>}><BusinessDialog key={draft.requestId} draft={draft} onClose={() => setDraft(current => current?.requestId === draft.requestId ? null : current)} onSaved={() => { setDraft(current => current?.requestId === draft.requestId ? null : current); window.dispatchEvent(new Event('chat-business-saved')); }}/></Suspense> : null}
    </Context.Provider>;
}

export function ThreadBusinessBadges({message}) {
    const business = useChatBusiness();
    if (!business || message.thread_root_message_id) return null;
    const links = business.links.filter(link => link.root_message_id === message.id);
    if (!links.length) return null;
    return <div className="chat-business-badges" aria-label="Linked Tasks and Complaints">{links.map(link => <button type="button" key={`${link.kind}:${link.target_id}`} onClick={() => business.open(link.kind, message, link)} title={`Open ${link.kind === 'TASK' ? 'Task' : 'Complaint'} ${link.cos_job_id}`}>
        {link.kind === 'TASK' ? '✓ Task' : '⚑ Complaint'} · {link.cos_job_id}
    </button>)}</div>;
}
